'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark hover:shadow-lg active:scale-95',
  secondary:
    'border-2 border-primary text-primary bg-white hover:bg-primary hover:text-white hover:shadow-lg active:scale-95',
  ghost: 'text-primary bg-transparent hover:bg-primary hover:bg-opacity-10 active:scale-95',
  danger: 'bg-red-600 text-white hover:bg-red-700 hover:shadow-lg active:scale-95',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-2 text-sm h-9',
  md: 'px-4 py-2.5 text-base h-10',
  lg: 'px-6 py-3 text-lg h-12',
};

interface ButtonProps {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export function Button({
  children,
  href,
  onClick,
  loading = false,
  disabled = false,
  className,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  type = 'button',
}: ButtonProps) {
  const baseStyles =
    'inline-flex items-center justify-center font-medium rounded-sm transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary';

  const buttonClass = cn(
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    fullWidth && 'w-full',
    className
  );

  const content = (
    <>
      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
      {children}
    </>
  );

  if (href) {
    return (
      <Link href={href}>
        <motion.button
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.98 }}
          className={buttonClass}
          disabled={disabled || loading}
        >
          {content}
        </motion.button>
      </Link>
    );
  }

  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={buttonClass}
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
    >
      {content}
    </motion.button>
  );
}
